import React from 'react';

import { PlatingForceType, Point2D, Point3D } from '@paragon/contract-library';
import { Segment3D } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { poundToInchScalar } from '../../library/unitScalarsForVisuals';
import { reverseShiftSegment } from '../../library/utilities/reverseShiftSegment';
import { SegmentLabel } from '../util/SegmentLabel';
import { VectorVisual, VectorVisualType } from '../util/VectorVisual';
import { MomentVisual } from './MomentVisual';

const getVectorTypeFromPlateForceType = (plateForceLoadType: PlatingForceType): VectorVisualType => {
  switch (plateForceLoadType) {
    case 'Compression':
      return VectorVisualType.Blue;
    case 'Tension':
      return VectorVisualType.Yellow;
    case 'Merged':
      return VectorVisualType.Green;
    case 'Exception':
      return VectorVisualType.Default;
    default:
      assertExhaustiveSwitch(plateForceLoadType, 'Plate force type was unrecognized');
  }
};

interface Props {
  readonly position: Point3D;
  readonly globalForceVector: Point2D;
  readonly platingForceType: PlatingForceType;
  readonly moment: number;
}

export function PlatingForceResultVisual({ position, globalForceVector, platingForceType, moment }: Props) {
  const forceVector = globalForceVector;
  let forceVectorSegment: Segment3D = {
    start: { x: 0, y: 0, z: 0 },
    end: { x: forceVector.x * poundToInchScalar, y: forceVector.y * poundToInchScalar, z: 0 },
  };
  if (platingForceType === 'Compression') {
    forceVectorSegment = reverseShiftSegment(forceVectorSegment);
  }
  const vectorVisualType = getVectorTypeFromPlateForceType(platingForceType);

  return (
    <object3D position={[position.x, position.y, position.z]}>
      <VectorVisual segment={forceVectorSegment} thickness={0.2} vectorVisualType={vectorVisualType} />
      <SegmentLabel segment={forceVectorSegment} text={`${forceVector.x.toFixed(0)},${forceVector.y.toFixed(0)}`} />
      <MomentVisual moment={moment} />
    </object3D>
  );
}
